import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { default as Divider } from 'antd/es/divider';
import type { ReactNode } from 'react';

import { formatDate, formatHours } from 'utils/dayjs';

import type { McpToken } from 'api';
import { Restricted } from 'containers/Restricted';

import {
  MCP_CARD_CREATED_TEST_ID,
  MCP_CARD_EXPIRES_TEST_ID,
  MCP_CARD_NAME_TEST_ID,
  MCP_CARD_PREVIEW_TEST_ID,
  MCP_CARD_REVOKE_TEST_ID,
  MCP_CARD_STATUS_TEST_ID,
  MCP_CARD_TEST_ID
} from './constants';
import { McpTokenStatusBadge } from './McpTokenStatusBadge';

type McpTokenCardProps = {
  token: McpToken;
  onRevoke: (token: McpToken) => void;
};

export const McpTokenCard = ({ token, onRevoke }: McpTokenCardProps) => (
  <div
    data-testid={MCP_CARD_TEST_ID(token.uuid)}
    className="flex w-[363px] max-w-full flex-col gap-3 rounded-lg border border-border-default bg-white p-4"
  >
    <div className="flex items-center gap-4 pb-4">
      <p
        data-testid={MCP_CARD_NAME_TEST_ID(token.uuid)}
        title={token.name}
        className="min-w-0 flex-1 truncate text-base font-medium leading-[22px] text-content-primary"
      >
        {token.name}
      </p>
      <McpTokenStatusBadge
        status={token.status}
        data-testid={MCP_CARD_STATUS_TEST_ID(token.uuid)}
      />
    </div>

    <div className="flex flex-col gap-2 p-1.5">
      <p className="text-xs font-semibold leading-[14px] text-content-primary">
        Token
      </p>
      <div className="flex items-center rounded-lg bg-surface-muted p-2">
        <span
          data-testid={MCP_CARD_PREVIEW_TEST_ID(token.uuid)}
          className="flex-1 font-mono text-xs leading-[22px] tracking-[0.36px] text-content-primary"
        >
          {token.token_preview}
        </span>
      </div>
    </div>

    <Divider className="m-0 mt-auto" />

    <div className="flex w-full flex-col gap-1.5">
      <DetailRow
        testId={MCP_CARD_CREATED_TEST_ID(token.uuid)}
        icon={<CalendarIcon className="size-[14px]" />}
        label="Created"
        value={formatDate(token.created_at, 'utc')}
      />
      <DetailRow
        testId={MCP_CARD_EXPIRES_TEST_ID(token.uuid)}
        title={`Expires ${formatDate(token.expires_at, 'utc')} at ${formatHours(
          token.expires_at
        )} UTC`}
        icon={<ClockIcon className="size-[14px]" />}
        label="Expires"
        value={formatDate(token.expires_at, 'utc')}
      />
    </div>

    {token.status === 'active' && (
      <Restricted requiredPermission="developers:manage">
        <button
          type="button"
          data-testid={MCP_CARD_REVOKE_TEST_ID(token.uuid)}
          onClick={() => onRevoke(token)}
          className="cursor-pointer self-start p-1.5 text-xs font-semibold leading-[14px] text-feedback-error hover:underline"
        >
          Revoke
        </button>
      </Restricted>
    )}
  </div>
);

type DetailRowProps = {
  testId: string;
  icon: ReactNode;
  label: string;
  value: string;
  title?: string;
};

const DetailRow = ({ testId, icon, label, value, title }: DetailRowProps) => (
  <div
    data-testid={testId}
    title={title}
    className="flex items-center justify-between p-1.5"
  >
    <div className="flex flex-1 items-center gap-1.5">
      <span className="flex size-5 items-center justify-center rounded bg-surface-muted p-[2.5px] text-content-secondary">
        {icon}
      </span>
      <span className="text-xs leading-[14px] text-content-secondary">
        {label}
      </span>
    </div>
    <span className="text-right text-xs font-semibold leading-[14px] text-content-primary">
      {value}
    </span>
  </div>
);
